"use client";

import React, { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/navigation";
import Image from "next/image";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  is404: boolean;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, is404: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Not everything thrown is an Error with a message - a thrown string or
    // object would make this dereference throw inside the boundary itself,
    // which React cannot recover from.
    return { hasError: true, is404: Boolean(error?.message?.includes("404")) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error);
  }

  render() {
    if (this.state.is404) {
      return <NotFoundPage />;
    }

    if (this.state.hasError) {
      return <ErrorContent />;
    }

    return this.props.children;
  }
}

const NotFoundPage: React.FC = () => {
  const router = useRouter();

  return (
    <div className="flex h-screen bg-gray-100 items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-gray-800">404</h1>
        <p className="text-lg text-gray-600">Page Not Found</p>
        <button
          onClick={() => router.push("/")}
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Go Home
        </button>
      </div>
    </div>
  );
};

const ErrorContent: React.FC = () => {
  const router = useRouter();

  return (
    <div className="flex h-screen bg-teal-600 items-center justify-center">
      <div className="w-full max-w-md p-6 text-center bg-white shadow-lg rounded-lg">
        <Image
          width={64}
          height={64}
          className="w-16 h-16 mx-auto mb-4"
          src="/next/assets/error.svg"
          alt="Error"
        />
        <h1 className="text-xl font-semibold text-gray-800">
          An unexpected error occurred.
        </h1>
        <p className="text-gray-600">
          If the issue persists, please contact support.
        </p>
        <button
          onClick={() => router.back()}
          className="mt-4 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
        >
          Go Back
        </button>
      </div>
    </div>
  );
};

export default ErrorBoundary;
